import { useEffect } from "react";

import { Box, Flex, Stack, xcss } from "@atlaskit/primitives-next";

import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";
import { ClientOnly } from "@/utils/client-only";

import { useFireAnalyticsEvents } from "../../../analytics";
import { SourceKey } from "../../../analytics/constants";
import { WelcomeBanner } from "../../common/welcome-banner";
import Flag from "../../contexts/FlagProvider";
import {
    getAccountOrgs,
    getAvailableProducts,
    getL1Assessments,
    getInstances,
    getMigrationPlanTotals,
    getOrgServers,
    setSelectedOrg,
    useFastshiftDispatch,
    useFastshiftSelector,
    FASTSHIFT_LOCALSTORAGE_KEYS,
    NO_ORG_ID
} from "../../state";

import { FastshiftResourcesSection } from "./fastshift-resources-section";
import { MigrationStatusTable } from "./migration-status-table";
import { MigrationSummarySection } from "./migration-summary-section";
import { useCustomerSupportRequests } from "./utils/use-customer-support-requests";

const migrationStatusTableContainerStyles = xcss({
    overflowY: "auto",
    width: "100%"
});

const contentContainerStyles = xcss({
    minWidth: "0px"
});

const useFastshiftStoreDataLoader = () => {
    const stargateClient = useBxpWebStargateClient();
    const dispatch = useFastshiftDispatch();

    const selectedOrg = useFastshiftSelector((state) => state.selectedOrg);
    const orgs = useFastshiftSelector((state) => state.orgs);

    const { getCustomerSupportRequests } = useCustomerSupportRequests();

    useEffect(() => {
        dispatch(getAccountOrgs(stargateClient));
        dispatch(getAvailableProducts(stargateClient));
    }, [dispatch, stargateClient]);

    useEffect(() => {
        getCustomerSupportRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedOrg && orgs.data.length > 0 && orgs.data[0]) {
            try {
                const storedOrgId = localStorage.getItem(
                    FASTSHIFT_LOCALSTORAGE_KEYS.SELECTED_ORG_ID
                );
                if (storedOrgId) {
                    const parsedStoredOrgId = JSON.parse(storedOrgId);
                    const matchedOrg = orgs.data.find(
                        (org) => org.id === parsedStoredOrgId
                    );
                    dispatch(setSelectedOrg(matchedOrg || orgs.data[0]));
                } else {
                    dispatch(setSelectedOrg(orgs.data[0]));
                }
            } catch (error) {
                console.error("Error parsing stored org ID:", error);
                // Fallback to first org if parsing fails
                dispatch(setSelectedOrg(orgs.data[0]));
            }
        }
    }, [selectedOrg, orgs.data, dispatch]);

    // fetch selected org server details when selected org changes
    useEffect(() => {
        if (selectedOrg && selectedOrg.id !== NO_ORG_ID) {
            dispatch(getOrgServers({ stargateClient, orgId: selectedOrg.id }));
            dispatch(
                getL1Assessments({ stargateClient, orgId: selectedOrg.id })
            );
            dispatch(
                getMigrationPlanTotals({
                    stargateClient,
                    orgId: selectedOrg.id
                })
            );
            dispatch(
                getInstances({
                    stargateClient,
                    orgId: selectedOrg.id
                })
            );
        }
    }, [dispatch, stargateClient, selectedOrg]);
};

export const FastshiftDashboard = () => {
    useFastshiftStoreDataLoader();
    const selectedOrg = useFastshiftSelector((state) => state.selectedOrg);
    const { fireScreenEvent } = useFireAnalyticsEvents();

    useEffect(() => {
        fireScreenEvent({
            name: SourceKey.FASTSHIFT_PORTAL_DASHBOARD_SCREEN,
            attributes: {
                loggedIn: true
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ClientOnly>
            <Flag.Provider>
                <Stack space="space.500">
                    <WelcomeBanner />
                    {selectedOrg && selectedOrg?.id !== NO_ORG_ID && (
                        <MigrationSummarySection />
                    )}
                    <Flex
                        justifyContent="space-between"
                        columnGap="space.300"
                        xcss={contentContainerStyles}
                    >
                        <Box xcss={migrationStatusTableContainerStyles}>
                            <MigrationStatusTable />
                        </Box>
                        <FastshiftResourcesSection />
                    </Flex>
                </Stack>
            </Flag.Provider>
        </ClientOnly>
    );
};
